import { useMemo, useEffect, useState } from 'react';
import {
  FormSubmitButton,
  FormTextInput,
  NavigateLinks,
  FormTextAreaInput,
  GlobalButton,
  DateInput,
  Authorize,
} from '@/components';
import { TypeAutocompleteInput } from '../../../Assets/Types/components';
import { Option } from '@/components/inputs/AutocompleteInput';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { PersonAdd as PersonAddIcon, Add as AddIcon } from '@mui/icons-material';
import { SearchUser } from '../../../../components';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { RequestSchema, defaultValues } from '../../models';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRequestActions } from '../../hooks';
import { requestsPageBreadcrumbs } from '../../config';
import { useRequestOptions } from '../../hooks';
import { ROLES } from '@/components/security/AppRoles';
import { ErrorPage } from '@/pages/Layout/pages';

import styles from './CreateRequest.module.css';

const CreateRequest = () => {
  const navigate = useNavigate();
  const [neededAt, setNeededAt] = useState<Date | null>(null);
  const [selectedTypeOption, setSelectedTypeOption] = useState<Option | null>(null);

  const { handleSubmit, control, reset, setValue } =
    useForm<RequestSchema>({
      resolver: zodResolver(RequestSchema),
      defaultValues,
      mode: 'onChange',
    });

  const breadcrumbs = useMemo(() => requestsPageBreadcrumbs(navigate), []);

  const { onCreateRequest, isCreatingRequest } = useRequestActions();
  const { isAssetsLoading } = useRequestOptions();


  useEffect(() => {
    setValue('status', 'pending');
  }, [setValue]);

  const onSubmit = async (data: RequestSchema) => {
    let finalNote = data.note || '';

    if (data.type === 'OTHER' && data.otherType) {
      const otherTypeInfo = `Tipo solicitado: ${data.otherType}`;
      finalNote = finalNote ? `${otherTypeInfo}. ${finalNote}` : otherTypeInfo;
    }

    const requestData = {
      ...data,
      neededAt: neededAt || undefined,
      note: finalNote,
      otherType: undefined,
    } as RequestSchema;

    await onCreateRequest(requestData, () => {
      reset();
      setNeededAt(null);
      setSelectedTypeOption(null);
    });
  };

  return (
    <Authorize
      permission={ROLES.INVENTORY.leader}
      fallback={<ErrorPage code={403} />}
    >
      <section className={styles.container}>
        <NavigateLinks actualPage='Crear solicitud' breadcrumbs={breadcrumbs} />
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <h2 className={styles.formTitle}>Crear solicitud</h2>

          <div className='flex gap-4 pt-5'>
            <SearchUser
              name='userId'
              placeholder='Usuario'
              control={control}
              required
              onUserSelect={(userId) => {
                setValue('userId', userId, {
                  shouldValidate: true,
                });
              }}
              showSelectedUserInfo={true}
            />
            <DateInput
              name='neededAt'
              placeholder='Fecha necesaria'
              value={neededAt}
              onChange={setNeededAt}
            />
          </div>
          <div className='flex justify-start mb-4'>
            <GlobalButton
              variant={GlobalButtonVariant.primaryButton}
              action={() => navigate('/inventory/users/create?from=requests')}
            >
              <PersonAddIcon />
              Crear usuario
            </GlobalButton>
          </div>
          <div className='flex gap-4'>
            <TypeAutocompleteInput
              name='type'
              placeholder={
                isAssetsLoading
                  ? 'Cargando tipos...'
                  : 'Seleccionar tipo de equipo'
              }
              value={selectedTypeOption}
              onChange={(option) => {
                setSelectedTypeOption(option);
                setValue('type', option?.data || '', { shouldValidate: true });
              }}
              onInputChange={() => {}}
              required
              showCreateButton={false}
            />
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={() => {
                const otherOption: Option = { label: 'Otro', data: 'OTHER' };
                setSelectedTypeOption(otherOption);
                setValue('type', 'OTHER', { shouldValidate: true });
              }}
            >
              <AddIcon />
              Otro
            </GlobalButton>
          </div>

          {selectedTypeOption?.data === 'OTHER' && (
            <div className='flex gap-4'>
              <FormTextInput
                name='otherType'
                placeholder='Especificar tipo de equipo'
                control={control}
                required
              />
            </div>
          )}

          <div className='flex gap-4'></div>
          <div className='flex gap-4'>
            <FormTextAreaInput
              name='note'
              placeholder='Motivo de la solicitud'
              control={control}
              rows={3}
            />
          </div>
          <FormSubmitButton
            isLoading={isCreatingRequest}
            label='Enviar'
            action={() => handleSubmit(onSubmit)({} as React.FormEvent)}
          />
        </form>
      </section>
    </Authorize>
  );
};

export default CreateRequest;
